import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Checkout() {
    const { cart, refresh } = useCart();
    const { user } = useAuth();
    const nav = useNavigate();
    const [addr, setAddr] = useState({ full_name: user?.name || "", phone: user?.phone || "", line1: "", line2: "", city: "", state: "", pincode: "", country: "India" });
    const [payment, setPayment] = useState("razorpay");
    const [coupon, setCoupon] = useState("");
    const [applied, setApplied] = useState(null);
    const [placing, setPlacing] = useState(false);

    const applyCoupon = async () => {
        if (!coupon) return;
        try {
            const { data } = await api.post("/coupons/validate", { code: coupon, subtotal: cart.subtotal });
            setApplied(data); toast.success(`Applied ${data.code}`);
        } catch (e) { toast.error(e.response?.data?.detail || "Invalid coupon"); setApplied(null); }
    };

    const discount = applied?.discount || 0;
    const shipping = (cart.subtotal - discount) >= 999 ? 0 : (cart.items.length ? 49 : 0);
    const total = Math.max(0, cart.subtotal - discount + shipping);

    const loadRazorpay = () => new Promise(resolve => {
        if (window.Razorpay) return resolve(true);
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });

    const placeOrder = async () => {
        if (!addr.full_name || !addr.phone || !addr.line1 || !addr.city || !addr.state || !addr.pincode) {
            toast.error("Please complete the shipping address");
            return;
        }
        setPlacing(true);
        try {
            const { data } = await api.post("/checkout/create", {
                address: addr,
                coupon_code: applied?.code || null,
                payment_method: payment,
            });
            if (payment === "cod") {
                toast.success("Order placed!");
                await refresh();
                nav(`/order/${data.order.id}/confirmation`);
                return;
            }
            // Razorpay
            if (!data.razorpay_key_id) {
                toast.error("Razorpay not configured. Please contact support.");
                setPlacing(false);
                return;
            }
            const ok = await loadRazorpay();
            if (!ok) { toast.error("Payment SDK failed to load"); setPlacing(false); return; }
            const rzp = new window.Razorpay({
                key: data.razorpay_key_id,
                amount: Math.round(total * 100),
                currency: "INR",
                name: "Clonzo",
                description: `Order ${data.order.order_number}`,
                order_id: data.order.razorpay_order_id,
                prefill: { name: addr.full_name, email: user.email, contact: addr.phone },
                theme: { color: "#FF3B30" },
                handler: async (res) => {
                    try {
                        await api.post("/checkout/verify", {
                            order_id: data.order.id,
                            razorpay_order_id: res.razorpay_order_id,
                            razorpay_payment_id: res.razorpay_payment_id,
                            razorpay_signature: res.razorpay_signature,
                        });
                        toast.success("Payment successful");
                        await refresh();
                        nav(`/order/${data.order.id}/confirmation`);
                    } catch (e) { toast.error("Payment verification failed"); }
                },
                modal: { ondismiss: () => setPlacing(false) },
            });
            rzp.open();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not place order");
            setPlacing(false);
        }
    };

    if (cart.items.length === 0) {
        return (
            <div className="max-w-[600px] mx-auto p-20 text-center">
                <p className="font-mono">Your cart is empty.</p>
                <button onClick={() => nav("/shop")} className="mt-4 bg-black text-white px-6 py-3 font-mono uppercase text-xs">Shop</button>
            </div>
        );
    }

    const F = ({label, value, onChange, ...rest}) => (
        <label className="block">
            <div className="font-mono text-[10px] uppercase text-neutral-500 mb-1">{label}</div>
            <input value={value} onChange={onChange} {...rest} className="w-full border-2 border-black p-3 font-mono text-sm" />
        </label>
    );

    return (
        <div className="max-w-[1200px] mx-auto px-4 md:px-10 py-10" data-testid="checkout-page">
            <h1 className="font-display text-5xl md:text-7xl tracking-tighter mb-8">Checkout</h1>
            <div className="grid md:grid-cols-[1fr_400px] gap-10">
                <div className="space-y-6">
                    <div className="border-2 border-black p-6">
                        <div className="font-mono text-xs uppercase tracking-widest mb-4">Shipping Address</div>
                        <div className="grid grid-cols-2 gap-4">
                            <F label="Full Name" value={addr.full_name} onChange={e => setAddr({...addr, full_name: e.target.value})} data-testid="ck-fullname" />
                            <F label="Phone" value={addr.phone} onChange={e => setAddr({...addr, phone: e.target.value})} data-testid="ck-phone" />
                            <label className="block col-span-2">
                                <div className="font-mono text-[10px] uppercase text-neutral-500 mb-1">Address Line 1</div>
                                <input value={addr.line1} onChange={e => setAddr({...addr, line1: e.target.value})} className="w-full border-2 border-black p-3 font-mono text-sm" data-testid="ck-line1" />
                            </label>
                            <label className="block col-span-2">
                                <div className="font-mono text-[10px] uppercase text-neutral-500 mb-1">Address Line 2 (optional)</div>
                                <input value={addr.line2} onChange={e => setAddr({...addr, line2: e.target.value})} className="w-full border-2 border-black p-3 font-mono text-sm" data-testid="ck-line2" />
                            </label>
                            <F label="City" value={addr.city} onChange={e => setAddr({...addr, city: e.target.value})} data-testid="ck-city" />
                            <F label="State" value={addr.state} onChange={e => setAddr({...addr, state: e.target.value})} data-testid="ck-state" />
                            <F label="Pincode" value={addr.pincode} onChange={e => setAddr({...addr, pincode: e.target.value})} data-testid="ck-pincode" />
                            <F label="Country" value={addr.country} onChange={e => setAddr({...addr, country: e.target.value})} data-testid="ck-country" />
                        </div>
                    </div>

                    <div className="border-2 border-black p-6">
                        <div className="font-mono text-xs uppercase tracking-widest mb-4">Payment</div>
                        <div className="flex flex-col gap-3">
                            <label className="flex items-center gap-3 border-2 border-black p-3 cursor-pointer">
                                <input type="radio" name="pm" checked={payment === "razorpay"} onChange={() => setPayment("razorpay")} data-testid="pm-razorpay" />
                                <div>
                                    <div className="font-mono text-xs uppercase">UPI / Card / Netbanking (Razorpay)</div>
                                    <div className="text-xs text-neutral-500">Secure checkout</div>
                                </div>
                            </label>
                            <label className="flex items-center gap-3 border-2 border-black p-3 cursor-pointer">
                                <input type="radio" name="pm" checked={payment === "cod"} onChange={() => setPayment("cod")} data-testid="pm-cod" />
                                <div className="font-mono text-xs uppercase">Cash on Delivery</div>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="border-2 border-black p-6 h-fit md:sticky md:top-24">
                    <div className="font-mono text-xs uppercase tracking-widest mb-4">Order Summary</div>
                    <div className="space-y-2 mb-4 max-h-52 overflow-auto">
                        {cart.items.map(it => (
                            <div key={`${it.product_id}-${it.size}`} className="flex justify-between font-mono text-xs">
                                <span className="truncate">{it.name} × {it.quantity} ({it.size})</span>
                                <span>₹{Math.round(it.price * it.quantity)}</span>
                            </div>
                        ))}
                    </div>
                    <div className="flex gap-2 mb-4">
                        <input value={coupon} onChange={e => setCoupon(e.target.value)} placeholder="Coupon code" className="flex-1 border-2 border-black p-2 font-mono text-xs uppercase" data-testid="coupon-input" />
                        <button onClick={applyCoupon} className="bg-black text-white px-3 font-mono uppercase text-[10px]" data-testid="apply-coupon-btn">Apply</button>
                    </div>
                    <div className="border-t-2 border-black pt-3 space-y-1 font-mono text-sm">
                        <div className="flex justify-between"><span>Subtotal</span><span>₹{Math.round(cart.subtotal)}</span></div>
                        {applied && <div className="flex justify-between text-[hsl(var(--accent))]"><span>Discount ({applied.code})</span><span>− ₹{Math.round(discount)}</span></div>}
                        <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? "Free" : `₹${shipping}`}</span></div>
                        <div className="flex justify-between font-display text-2xl border-t-2 border-black pt-2"><span>Total</span><span>₹{Math.round(total)}</span></div>
                    </div>
                    <button disabled={placing} onClick={placeOrder} className="mt-4 w-full bg-black text-white py-3 font-mono uppercase text-xs tracking-widest brutal-shadow disabled:opacity-50" data-testid="place-order-btn">
                        {placing ? "Placing…" : payment === "cod" ? "Place COD Order" : "Pay Now"}
                    </button>
                </div>
            </div>
        </div>
    );
}
